import React, { useEffect, useState } from "react";
import { Sparkles, Crown, TrendingUp, Users, RefreshCw, ExternalLink, Send, Check, Clock } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const NUDGE_COOLDOWN_DAYS = 7;

/**
 * Soul-richness leaderboard for the JLR admin tab. Shows the top members
 * ranked by how much of their Soul Form is populated — "narrative-ready"
 * members ready for personalised Mira touches, Welcome Letters, etc.
 *
 * Includes a 1-click "Send nudge" button per row that sends a gentle
 * Soul Form re-invite from `request@concierges.in`. 7-day cooldown
 * enforced server-side (HTTP 409) so accidental double-clicks are safe.
 *
 * Props:
 *   fixedClientId — "jlr" (passed by AdminPanel.js)
 */
export default function SoulRichnessLeaderboardTile({ fixedClientId = "jlr" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [limit, setLimit] = useState(25);
  const [nudgingId, setNudgingId] = useState(null);
  // Local-only override so the row updates instantly without a refetch
  const [recentlyNudged, setRecentlyNudged] = useState({});

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const token = localStorage.getItem("scaleboard_token");
      const r = await fetch(
        `${API}/api/soul-engine/${fixedClientId}/richness-leaderboard?limit=${limit}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setData(j);
    } catch (e) {
      setErr(e.message || "Failed to load");
    } finally { setLoading(false); }
  };

  const sendNudge = async (member) => {
    if (!member?.email) {
      toast.error(`${member.name} has no email — can't nudge.`);
      return;
    }
    if (!window.confirm(
      `Send gentle Soul Form nudge to ${member.name} (${member.email})?\n\n` +
      "They'll receive a warm note from request@concierges.in with their " +
      "private Soul Profile link. 7-day cooldown applies after.",
    )) return;
    setNudgingId(member.zoho_contact_id);
    try {
      const token = localStorage.getItem("scaleboard_token");
      const r = await fetch(
        `${API}/api/soul-form-admin/${fixedClientId}/nudge/${member.zoho_contact_id}`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } },
      );
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || `HTTP ${r.status}`);
      toast.success(`Nudged ${member.name} · sent to ${member.email}`);
      setRecentlyNudged((prev) => ({
        ...prev,
        [member.zoho_contact_id]: new Date().toISOString(),
      }));
    } catch (e) {
      toast.error(`Failed: ${e.message}`);
    } finally {
      setNudgingId(null);
    }
  };

  // Returns null (can nudge) or ISO timestamp (within cooldown)
  const lastNudgedFor = (member) => {
    const local = recentlyNudged[member.zoho_contact_id];
    if (local) return local;
    return member.last_nudged_at || null;
  };
  const isOnCooldown = (member) => {
    const last = lastNudgedFor(member);
    if (!last) return false;
    const ageMs = Date.now() - new Date(last).getTime();
    return ageMs < NUDGE_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
  };
  const cooldownLabel = (member) => {
    const last = lastNudgedFor(member);
    if (!last) return "";
    const d = new Date(last);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
  };

  useEffect(() => { load(); }, [fixedClientId, limit]);

  const buckets = data?.buckets || { empty: 0, low: 0, mid: 0, high: 0 };
  const total = data?.total || 0;
  const pct = (n) => total ? Math.round(100 * n / total) : 0;

  return (
    <div
      data-testid="soul-richness-leaderboard-tile"
      className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-rose-50 p-6 shadow-sm"
    >
      <div className="mb-5 flex items-start justify-between">
        <div>
          <div className="mb-1 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-500" />
            <h3 className="font-serif text-xl font-semibold text-slate-900">
              Soul-richness leaderboard
            </h3>
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-700">
              JLR · narrative-ready
            </span>
          </div>
          <p className="text-sm italic text-slate-500">
            Members whose Soul Forms have enough detail for Mira to draft personalised
            outreach. Click "Send nudge" on a row to email a gentle re-invite from
            <strong className="font-semibold text-slate-700"> request@concierges.in</strong>.
          </p>
        </div>
        <button
          data-testid="soul-richness-refresh"
          onClick={load}
          disabled={loading}
          className="rounded-full border border-slate-200 bg-white p-2 transition hover:bg-slate-50 disabled:opacity-40"
          title="Refresh"
        >
          <RefreshCw className={`h-4 w-4 text-slate-500 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {/* Bucket stats */}
      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <BucketCard data-testid="bucket-empty" label="Empty" count={buckets.empty} pct={pct(buckets.empty)} colour="slate" hint="0 fields" />
        <BucketCard data-testid="bucket-low" label="Low" count={buckets.low} pct={pct(buckets.low)} colour="rose" hint="< 25%" />
        <BucketCard data-testid="bucket-mid" label="Mid" count={buckets.mid} pct={pct(buckets.mid)} colour="amber" hint="25–60%" />
        <BucketCard data-testid="bucket-high" label="Narrative-ready" count={buckets.high} pct={pct(buckets.high)} colour="emerald" hint="≥ 60%" />
      </div>

      {/* Errors / loading */}
      {err ? (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {err}
        </div>
      ) : null}
      {loading && !data ? (
        <div className="py-8 text-center text-sm text-slate-400">Loading leaderboard…</div>
      ) : null}

      {/* Leaderboard */}
      {data && data.members?.length ? (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="soul-richness-table">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left font-medium">#</th>
                <th className="px-3 py-2 text-left font-medium">Member</th>
                <th className="px-3 py-2 text-left font-medium">Membership</th>
                <th className="px-3 py-2 text-left font-medium">Soul richness</th>
                <th className="px-3 py-2 text-left font-medium">City</th>
                <th className="px-3 py-2 text-right font-medium">Nudge</th>
              </tr>
            </thead>
            <tbody>
              {data.members.map((m, i) => {
                const onCooldown = isOnCooldown(m);
                const isBusy = nudgingId === m.zoho_contact_id;
                const noEmail = !m.email;
                return (
                  <tr key={m.zoho_contact_id || i}
                      data-testid={`soul-richness-row-${i}`}
                      className="border-t border-slate-100 transition hover:bg-amber-50/40">
                    <td className="px-3 py-2 text-slate-400">
                      {i === 0 ? <Crown className="h-3.5 w-3.5 text-amber-500" /> : i + 1}
                    </td>
                    <td className="px-3 py-2 font-medium text-slate-900">
                      {m.name}
                      {m.email ? (
                        <div className="text-[10px] font-normal text-slate-400">{m.email}</div>
                      ) : (
                        <div className="text-[10px] font-normal italic text-rose-400">no email on file</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-slate-500">
                      {m.membership_no || <span className="italic text-slate-300">—</span>}
                      {m.model_name ? <span className="ml-1 text-[10px] text-slate-400">· {m.model_name}</span> : null}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-20 overflow-hidden rounded-full bg-slate-100">
                          <div
                            className="h-full bg-gradient-to-r from-amber-400 to-rose-500"
                            style={{ width: `${m.richness_pct}%` }}
                          />
                        </div>
                        <span className="text-[11px] font-medium text-slate-600">
                          {m.richness_score}/{data.total_fields_tracked} · {m.richness_pct}%
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-slate-500">{m.city || <span className="italic text-slate-300">—</span>}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        data-testid={`soul-richness-nudge-${i}`}
                        onClick={() => sendNudge(m)}
                        disabled={isBusy || onCooldown || noEmail}
                        title={
                          noEmail ? "Member has no email on file" :
                          onCooldown ? `Already nudged on ${cooldownLabel(m)} · 7-day cooldown` :
                          "Send gentle Soul Form re-invite"
                        }
                        className={`inline-flex items-center rounded-md px-2.5 py-1 text-[11px] font-medium transition ${
                          isBusy
                            ? "bg-amber-100 text-amber-700"
                            : onCooldown
                            ? "border border-emerald-200 bg-emerald-50 text-emerald-700"
                            : noEmail
                            ? "cursor-not-allowed border border-slate-200 bg-slate-50 text-slate-300"
                            : "border border-slate-200 bg-white text-slate-700 hover:border-amber-300 hover:bg-amber-50 hover:text-amber-700"
                        }`}
                      >
                        {isBusy ? (
                          <><RefreshCw className="mr-1 h-3 w-3 animate-spin" />Sending…</>
                        ) : onCooldown ? (
                          <><Check className="mr-1 h-3 w-3" />Nudged {cooldownLabel(m)}</>
                        ) : noEmail ? (
                          <><Clock className="mr-1 h-3 w-3" />No email</>
                        ) : (
                          <><Send className="mr-1 h-3 w-3" />Send nudge</>
                        )}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* Pagination toggle */}
      <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
        <div>
          <Users className="mr-1 inline h-3.5 w-3.5" />
          {total.toLocaleString()} JLR members tracked · {data?.total_fields_tracked || 0} Soul fields per profile · 7-day cooldown between nudges
        </div>
        <div className="flex items-center gap-1">
          <span>Show</span>
          {[10, 25, 50, 100].map((n) => (
            <button
              key={n}
              data-testid={`soul-richness-limit-${n}`}
              className={`rounded-md px-2 py-0.5 font-medium transition ${
                limit === n
                  ? "bg-slate-900 text-white"
                  : "text-slate-500 hover:bg-slate-100"
              }`}
              onClick={() => setLimit(n)}
            >
              {n}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

const BucketCard = ({ label, count, pct, colour, hint, "data-testid": testid }) => {
  const colourClass = {
    slate: "from-slate-50 to-slate-100 text-slate-700 ring-slate-200",
    rose: "from-rose-50 to-rose-100 text-rose-700 ring-rose-200",
    amber: "from-amber-50 to-amber-100 text-amber-700 ring-amber-200",
    emerald: "from-emerald-50 to-emerald-100 text-emerald-700 ring-emerald-200",
  }[colour] || "from-slate-50 to-slate-100 text-slate-700 ring-slate-200";
  return (
    <div data-testid={testid}
         className={`rounded-xl bg-gradient-to-br p-3 ring-1 ${colourClass}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-wider">{label}</span>
        <TrendingUp className="h-3 w-3 opacity-50" />
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{count.toLocaleString()}</div>
      <div className="text-[10px] opacity-70">{hint} · {pct}% of base</div>
    </div>
  );
};
